import type { CSSProperties, ReactNode } from "react";
import { cn } from "@nexus/ui";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { DragHandleProps } from "../lib/admin-vendor-form.utils";

interface SortableRowProps {
  id: string;
  children: (dragHandle: DragHandleProps) => ReactNode;
}

/** Wraps a draggable row, wiring dnd-kit transforms and exposing handle props. */
export function SortableRow({ id, children }: SortableRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : undefined,
    zIndex: isDragging ? 10 : undefined,
    position: isDragging ? "relative" : undefined,
  };
  return (
    <div ref={setNodeRef} style={style}>
      {children({ attributes, listeners, isDragging })}
    </div>
  );
}

interface SectionEmptyProps {
  icon: ReactNode;
  label: string;
  isError?: boolean;
}

/** Dashed placeholder shown when a packages/questions section has no rows yet. */
export function SectionEmpty({ icon, label, isError = false }: SectionEmptyProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center gap-2 rounded-md border border-dashed bg-muted/20 px-4 py-8 text-center",
        isError ? "border-destructive/60" : "border-border",
      )}
    >
      {icon}
      <p className={cn("max-w-xs text-xs", isError ? "text-destructive" : "text-muted-foreground")}>
        {label}
      </p>
    </div>
  );
}
